import { ChangeEvent, Dispatch, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import cn from 'classnames';

import { Modal } from 'UIPackage/ModalModule/components/Modal';

import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { SelectInput } from '../../../UIPackage/MainModule/components/SelectInput';
import { KnmCollapse } from '../../MainModule/components/KnmCollapse';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRiskCategory248 } from '../../../APIPackage/DictionariesModule/interfaces/IRiskCategory248';
import { IDangerClass248 } from '../../../APIPackage/DictionariesModule/interfaces/IDangerClass248';
import styles from './KnmErvkObjectsModal.module.scss';
import { IKnmState } from '../../MainModule/types/IKnmState';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { IErvkObjectsOfControl } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControl';
import { knmInfoGetErvkObjectsOfControl, knmClearErvkObjectsOfControl } from '../../MainModule/actions/knmActions';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getErvkObjectsOfControl } from '../../MainModule/selectors/knmSelectors';
import { IErvkObjectsOfControlSubjectData } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControlSubjectData';
import { KnmSubjectsForErvkObjectsModal } from './KnmSubjectsForErvkObjectsModal';

interface IKnmErvkObjectsModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (objects: IErvkObjectsOfControl[], subjects: IErvkObjectsOfControlSubjectData[]) => void;
  watchOnly: boolean;
  riskCategoriesSelectOptions: IDictionarySelectOption<IRiskCategory248>[] | undefined;
  dangerClassesSelectOptions: IDictionarySelectOption<IDangerClass248>[] | undefined;
  kindKnmCode: string | undefined;
  isDecisionPassport?: boolean;
}

interface IKnmErvkObjectsModalStateProps {
  ervkObjectsOfControl: IKnmState['ervkObjectsOfControl'] | undefined;
}

export const KnmErvkObjectsModalWithoutStore = ({
  visible,
  onClose,
  onSubmit,
  watchOnly,
  riskCategoriesSelectOptions,
  dangerClassesSelectOptions,
  ervkObjectsOfControl,
  kindKnmCode,
  isDecisionPassport,
}: IKnmErvkObjectsModalProps & IKnmErvkObjectsModalStateProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isSubjectModalVisible, setIsSubjectModalVisible] = useState<boolean>(false);
  const [selectedErvkObjects, setSelectedErvkObjects] = useState<IErvkObjectsOfControl[]>([]);
  const [textFilter, setTextFilter] = useState<string | undefined>('');
  const [cosIdFilter, setCosIdFilter] = useState<string | undefined>('');
  const [filterRiskCategory, setFilterRiskCategory] = useState<IDictionarySelectOption<IRiskCategory248> | undefined>(
    undefined,
  );
  const [filterDangerClass, setFilterDangerClass] = useState<IDictionarySelectOption<IDangerClass248> | undefined>(
    undefined,
  );
  const [isTableVisible, setIsTableVisible] = useState<boolean>(false);

  const isCosIdFilled = !!cosIdFilter;
  const isTextFilled = !!textFilter;

  useEffect(() => {
    if (visible) {
      setIsTableVisible(false);
      setSelectedErvkObjects([]);
      setIsSubjectModalVisible(false);
      setFilterRiskCategory(undefined);
      setFilterDangerClass(undefined);
      setTextFilter('');
      setCosIdFilter('');
      dispatch(knmClearErvkObjectsOfControl());
    }
  }, [visible]);

  useEffect(() => {
    if (ervkObjectsOfControl?.length && ervkObjectsOfControl?.length > 0) {
      setIsTableVisible(true);
    }
  }, [ervkObjectsOfControl]);

  const handleLoad = useCallback(() => {
    setIsTableVisible(true);
    setSelectedErvkObjects([]);
    dispatch(
      knmInfoGetErvkObjectsOfControl(
        cosIdFilter,
        textFilter,
        filterRiskCategory?.value.dictVersionId
          ? [filterRiskCategory?.value.dictVersionId]
          : riskCategoriesSelectOptions?.map(x => x.value.dictVersionId),
        filterDangerClass?.value.dictVersionId
          ? [filterDangerClass?.value.dictVersionId]
          : dangerClassesSelectOptions?.map(x => x.value.dictVersionId),
        isDecisionPassport,
      ),
    );
  }, [
    cosIdFilter,
    textFilter,
    filterRiskCategory,
    filterDangerClass,
    riskCategoriesSelectOptions,
    dangerClassesSelectOptions,
  ]);

  const infoText =
    'Выберите объект контроля, осуществив его поиск по номеру объекта из ЕРВК или по категории риска/классу\n' +
    'опасности. В случае отсутствия необходимого объекта контроля или наличии ошибок необходимо внести\n' +
    'данные в ЕРВК.';

  const isErvkObjectsEquals = (val1: IErvkObjectsOfControl, val2: IErvkObjectsOfControl): boolean => {
    return val1.ervkId === val2.ervkId;
  };

  const isSelected = (value: IErvkObjectsOfControl): boolean => {
    return !!selectedErvkObjects.find(item => isErvkObjectsEquals(item, value));
  };
  const handleSelect = (value: IErvkObjectsOfControl) => {
    if (isSelected(value)) {
      setSelectedErvkObjects(selectedErvkObjects.filter(item => !isErvkObjectsEquals(item, value)));
    } else setSelectedErvkObjects([...selectedErvkObjects, value]);
  };

  const handleSelectAll = () => {
    setSelectedErvkObjects(ervkObjectsOfControl || []);
  };

  const handleDismissAll = () => {
    setSelectedErvkObjects([]);
  };

  const handleTextFilter = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTextFilter(e.target.value);
  }, []);

  const handleCosIdFilter = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setCosIdFilter(e.target.value);
  }, []);

  const handleRiskCategoryFilter = useCallback((option: IDictionarySelectOption<IRiskCategory248> | undefined) => {
    setFilterRiskCategory(option);
  }, []);

  const handleDangerClassFilter = useCallback((option: IDictionarySelectOption<IDangerClass248> | undefined) => {
    setFilterDangerClass(option);
  }, []);

  const clearFilter = () => {
    setFilterRiskCategory(undefined);
    setFilterDangerClass(undefined);
    setTextFilter('');
    setCosIdFilter('');
  };

  const organizationInfo = (org: IErvkObjectsOfControlSubjectData) => {
    const inn = org.inn ? `ИНН: ${org.inn} ` : '';
    const ogrn = org.ogrn ? `ОГРН: ${org.ogrn} ` : '';
    const name = org.organizationFullName ? `Наименование: ${org.organizationFullName} ` : '';
    const snils = org.snils ? `СНИЛС: ${org.snils} ` : '';
    const kpp = org.kpp ? `КПП: ${org.kpp} ` : '';
    const rafp = org.rafp ? `НЗА: ${org.rafp} ` : '';
    const fio = org.firstName ? `ФИО: ${org.firstName} ${org.middleName} ${org.surName}` : '';
    const nationality = org.nationality
      ? `Государство, в котором указанное юридическое лицо зарегистрировано: ${org.nationality} `
      : '';

    return inn + ogrn + name + fio + snils + kpp + rafp + nationality;
  };

  const handleSubmit = () => {
    if (selectedErvkObjects) {
      setIsSubjectModalVisible(true);
    }
  };

  const closeSubjectModal = () => {
    setIsSubjectModalVisible(false);
  };

  return (
    <Modal visible={visible} onClose={onClose} modalBodyClassName={styles.ModalBody} modalClassName={styles.Modal}>
      {isSubjectModalVisible && (
        <KnmSubjectsForErvkObjectsModal
          onClose={closeSubjectModal}
          visible={isSubjectModalVisible}
          onSubmit={onSubmit}
          ervkObjects={selectedErvkObjects}
          kindKnmCode={kindKnmCode}
        />
      )}

      {!isSubjectModalVisible && (
        <>
          <ModalHeader title="Выбор объекта контроля" />
          <ModalBody overflow="scroll">
            <KnmCollapse title="Поиск" openByDefault>
              <>
                <Row>
                  <FieldBlock label="Номер объекта контроля" color="black">
                    <TextInput
                      placeholder="Введите номер объекта контроля"
                      value={cosIdFilter}
                      onChange={handleCosIdFilter}
                      disabled={isTextFilled}
                    />
                  </FieldBlock>
                </Row>
                <Row>
                  <FieldBlock label="Наименование/адрес объекта контроля" color="black">
                    <TextInput
                      placeholder="Введите наименование или местонахождение объекта контроля"
                      value={textFilter}
                      onChange={handleTextFilter}
                      disabled={isCosIdFilled}
                    />
                  </FieldBlock>
                </Row>
                <Row>
                  <FieldBlock label="Категория риска" color="black" labelSize="col-3" size="half">
                    <SelectInput
                      options={riskCategoriesSelectOptions}
                      disabled={watchOnly}
                      size="medium"
                      value={filterRiskCategory}
                      onChange={handleRiskCategoryFilter}
                    />
                  </FieldBlock>
                  <FieldBlock label="Класс опасности" color="black" labelSize="col-3" size="half">
                    <SelectInput
                      options={dangerClassesSelectOptions}
                      disabled={watchOnly}
                      size="medium"
                      value={filterDangerClass}
                      onChange={handleDangerClassFilter}
                    />
                  </FieldBlock>
                </Row>
                <div className={styles.SearchButtonsRow}>
                  <Button
                    text="Искать"
                    color="primary"
                    size="medium"
                    type="button"
                    className={styles.LeftButton}
                    onClick={handleLoad}
                  />
                  <Button text="Очистить фильтр" size="medium" type="button" onClick={clearFilter} />
                </div>
              </>
            </KnmCollapse>
            <Row notSpace>
              <Button
                text="Выбрать все"
                size="medium"
                type="button"
                className={styles.LeftButton}
                onClick={handleSelectAll}
              />
              <Button text="Сбросить" size="medium" type="button" onClick={handleDismissAll} />
            </Row>

            <Row>
              <div className={styles.InformationSupportBody}>
                <span className={styles.InfoIcon}></span>
                <span className={styles.InformationSupportText}>{infoText}</span>
              </div>
            </Row>

            {isTableVisible && (
              <div className={styles.Table}>
                <div className={styles.TableHeader}>
                  <div></div>
                  <div>№</div>
                  <div>НАИМЕНОВАНИЕ</div>
                  <div>КОНТРОЛИРУЕМОЕ ЛИЦО</div>
                  <div>МЕСТОНАХОЖДЕНИЯ</div>
                  <div>КАТЕГОРИЯ РИСКА</div>
                  <div>КЛАСС ОПАСНОСТИ</div>
                </div>
                {!ervkObjectsOfControl?.length ? (
                  <div className={styles.TableBodyEmpty}>
                    <span>Данные отсутствуют</span>
                  </div>
                ) : (
                  <div className={styles.TableBody}>
                    <div className={styles.TableContent}>
                      {ervkObjectsOfControl.map((item, index) => (
                        <div
                          key={`ervkObjectsOfControlItem[${index}]`}
                          className={cn(styles.TableRow, {
                            [styles.TableRowActive]: isSelected(item),
                          })}
                          onClick={() => handleSelect(item)}
                        >
                          <div>
                            <Checkbox theme="system" disabled={watchOnly} checked={isSelected(item)} />
                          </div>
                          <div>{item.cosId}</div>
                          <div>{item.name}</div>
                          <div>{item.subjects.length ? organizationInfo(item.subjects[0]) : ''}</div>
                          <div>{item.address}</div>
                          <div>{item.risk?.title}</div>
                          <div>{item.dangerClass?.title}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalActions>
            <Button
              text="Добавить"
              color="primary"
              size="medium"
              onClick={handleSubmit}
              disabled={!selectedErvkObjects.length}
            />
            <Button text="Отмена" size="medium" onClick={onClose} />
          </ModalActions>
        </>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IKnmErvkObjectsModalStateProps => ({
  ervkObjectsOfControl: getErvkObjectsOfControl(state),
});

export const KnmErvkObjectsModal = connect(mapStateToProps)(KnmErvkObjectsModalWithoutStore);
